import React, { useMemo, useState, useEffect } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PlusIcon, PencilIcon, Trash2, Sparkles, Layers } from 'lucide-react';
import {
  useAttributes,
  useUpdateAttribute,
  useDeleteAttribute,
  useCreateAttribute,
  useAttributeGroups,
} from './api';
import { useGlobalAttributes } from '@/hooks/useGlobalAttributes';
import type { Attribute, AttributeGroup } from './types';
import LocaleChannelSelector from '@/features/attributes/LocaleChannelSelector';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

import { useQueryClient, useQuery } from '@tanstack/react-query';
import { ChannelCode, LocaleCode } from '@/services/types';
import axiosInstance from '@/lib/enhancedAxiosInstance';
import { useFamilyAttributeGroups } from '@/hooks/useFamilyAttributeGroups';
import { useOrgSettings } from '@/hooks/useOrgSettings';

import { useAuth } from '@/contexts/AuthContext';
import { ENABLE_EFFECTIVE_ATTRIBUTES_V2 } from '@/config/featureFlags';
import { EffectiveAttributesView } from './EffectiveAttributesView';
import { useProductDetailPageTranslations } from '@/hooks/useProductDetailPageTranslations';
import { useTranslation } from 'react-i18next';

// Enhanced components
import {
  EnhancedAttributeValueEditor,
  type EnhancedAttribute,
  type EnhancedAttributeValue,
} from '@/features/attributes/EnhancedAttributeValueEditor';
import { EnhancedAttributeModal } from '@/features/attributes/EnhancedAttributeModal';
import AddAttributeModal from '@/features/attributes/AddAttributeModal';
import { logError } from '@/utils/errorHandler';

interface ProductAttributesPanelProps {
  productId: string;
  locale?: string;
  channel?: string;
  familyId?: number; // can be undefined but not null
  enabled?: boolean; // Add enabled prop to control when to fetch data
}

export function ProductAttributesPanel({
  productId,
  locale,
  channel,
  familyId: propFamilyId,
  enabled,
}: ProductAttributesPanelProps) {
  useAuth();
  const queryClient = useQueryClient();

  // i18n: Initialize translations hook
  const { getAttributesLabels } = useProductDetailPageTranslations();
  const { t } = useTranslation('translation');

  // IMPORTANT NOTE FOR PRODUCT FAMILY UPDATES:
  // When updating a product's family in other components (like Basic Info tab),
  // you must invalidate the product query to update the familyId in this component:
  //
  // queryClient.invalidateQueries(['product', productId])
  //
  // Otherwise, this component will continue using the old familyId until page reload.

  const { locales, channels, defaultLocale, defaultChannel } = useOrgSettings();

  // Use org defaults if props are not provided
  const [selectedLocale, setSelectedLocale] = useState<LocaleCode>(locale ?? defaultLocale);
  const [selectedChannel, setSelectedChannel] = useState<ChannelCode>(
    channel ?? defaultChannel?.code ?? (channels.length > 0 ? channels[0].code : '')
  );

  // Keep selectedLocale/Channel in sync with org defaults if props are not provided
  useEffect(() => {
    if (!locale && defaultLocale && selectedLocale !== defaultLocale) {
      setSelectedLocale(defaultLocale);
    }
    if (!channel && defaultChannel?.code && selectedChannel !== defaultChannel.code) {
      setSelectedChannel(defaultChannel.code);
    }
  }, [locale, channel, defaultLocale, defaultChannel, selectedLocale, selectedChannel]);

  // Add flag to track if we're in create mode
  const isCreateMode = !productId;

  // Fetch product data to access family
  const { data: productData } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => axiosInstance.get(`/api/products/${productId}/`).then(r => r.data),
    enabled: !!productId,
  });

  // Prefer familyId from props over product data
  const familyId = propFamilyId !== undefined ? propFamilyId : productData?.family;
  const hasFamily = Boolean(familyId);

  // Refetch family groups when props.familyId changes
  useEffect(() => {
    if (propFamilyId !== undefined) {
      queryClient.invalidateQueries({
        queryKey: [
          'familyAttributeGroups',
          propFamilyId,
          String(selectedLocale || 'all'),
          String(selectedChannel || 'all'),
        ],
      });
    }
  }, [propFamilyId, queryClient, selectedLocale, selectedChannel]);

  // Fetch family attribute groups if family exists
  const familyGroupsRaw = useFamilyAttributeGroups(familyId, selectedLocale, selectedChannel);
  const familyGroups: AttributeGroup[] = familyGroupsRaw.data ?? [];
  const isLoadingFamilyGroups = familyGroupsRaw.isLoading;
  const familyError = familyGroupsRaw.error;

  // Fetch product attribute groups (overrides)
  const attributeGroupsRaw = useAttributeGroups(
    productId,
    selectedLocale,
    selectedChannel,
    enabled
  );
  const attributeGroups: AttributeGroup[] = attributeGroupsRaw.data ?? [];

  // Compute sourceGroups: prefer familyGroups if hasFamily, else attributeGroups
  const sourceGroups = (hasFamily ? familyGroups : attributeGroups) as AttributeGroup[];

  // Add additional fallback for empty groups
  const hasNoGroups = !sourceGroups || sourceGroups.length === 0;

  // Enhanced editing state - replace legacy editing system
  const [editingAttributeId, setEditingAttributeId] = useState<number | null>(null);
  const [isCreateAttributeModalOpen, setIsCreateAttributeModalOpen] = useState(false);
  const [isAddAttributeModalOpen, setIsAddAttributeModalOpen] = useState(false);

  // Call useAttributes with correct arguments for the local implementation
  const attributesHook = useAttributes(productId, selectedLocale, selectedChannel);
  const { isPending, isError } = attributesHook;
  const updateMutation = useUpdateAttribute(productId, selectedLocale, selectedChannel);
  const deleteMutation = useDeleteAttribute(productId, selectedLocale, selectedChannel);
  const createMutation = useCreateAttribute(productId, selectedLocale, selectedChannel);
  const { data: globalAttrData } = useGlobalAttributes();
  const allAttributes = globalAttrData?.attributes || [];

  // Build grouped data from sourceGroups, then add any data attributes
  const grouped: Record<string, Attribute[]> = useMemo(() => {
    const map: Record<string, Attribute[]> = {};

    // Handle the case where sourceGroups is undefined or empty
    if (sourceGroups && Array.isArray(sourceGroups) && sourceGroups.length > 0) {
      sourceGroups.forEach((g: AttributeGroup) => {
        if (g && g.name) map[g.name] = [];
      });
    }

    // Always ensure 'Ungrouped' exists
    map[getAttributesLabels.groups.ungrouped] = [];

    // Use attribute values from the attributes hook instead of productData?.attributes
    const attributeValues = attributesHook.data?.attributes || [];

    // Ensure attributeValues is an array before iterating
    if (Array.isArray(attributeValues)) {
      attributeValues.forEach((attrVal: Attribute) => {
        const attrId = Number((attrVal as any).attribute ?? attrVal.id);

        // Only look for a group if we have sourceGroups
        let foundGroup = false;
        if (sourceGroups && sourceGroups.length > 0) {
          const grp = sourceGroups.find(
            (g: AttributeGroup) =>
              g.items &&
              Array.isArray(g.items) &&
              g.items.some((item: any) => Number(item.attribute) === attrId)
          );
          if (grp) {
            map[grp.name].push(attrVal);
            foundGroup = true;
          }
        }

        if (!foundGroup) map[getAttributesLabels.groups.ungrouped].push(attrVal);
      });
    }

    return map;
  }, [sourceGroups, attributesHook.data, getAttributesLabels.groups.ungrouped]);

  // Translation configuration accessed via hook above

  // Enhanced attribute value save handler
  const handleEnhancedSave = async (
    attributeId: number,
    value: any,
    locale?: string,
    channel?: string
  ) => {
    try {
      // Get the attribute definition to check if it supports locale/channel
      const attrDef = allAttributes.find((a: any) => Number(a.id) === attributeId);

      // Only send locale/channel if the attribute supports them
      const saveLocale =
        attrDef?.is_localisable && (locale || selectedLocale) !== 'default'
          ? locale || selectedLocale
          : undefined;
      const saveChannel =
        attrDef?.is_scopable && (channel || selectedChannel) !== 'default'
          ? channel || selectedChannel
          : undefined;

      const saveData = {
        attribute: attributeId,
        value,
        locale: saveLocale,
        channel: saveChannel,
      };

      // Find existing attribute value to determine if update or create
      const existingAttr = attributesHook.data?.attributes?.find(
        (attr: Attribute) => Number((attr as any).attribute ?? attr.id) === attributeId
      );

      if (existingAttr) {
        await updateMutation.mutateAsync({
          id: existingAttr.id,
          payload: saveData,
        });
      } else {
        await createMutation.mutateAsync({
          attributeId: attributeId,
          value: value,
          locale: saveLocale,
          channel: saveChannel,
        });
      }

      setEditingAttributeId(null);
      toast.success(`Attribute value saved successfully`);
    } catch (error) {
      logError(
        'Failed to save attribute value',
        {
          component: 'ProductAttributesPanel',
          action: 'handleEnhancedSave',
          metadata: { attributeId, productId, locale, channel, error },
        },
        { showToast: true, userFacing: true }
      );
      toast.error('Failed to save attribute value');
      throw error;
    }
  };

  // Enhanced cancel handler
  const handleEnhancedCancel = () => {
    setEditingAttributeId(null);
  };

  // Convert legacy attribute to enhanced attribute
  const convertToEnhancedAttribute = (attr: Attribute): EnhancedAttribute => {
    const attrId = Number((attr as any).attribute ?? attr.id);
    const attrDef = allAttributes.find((a: any) => Number(a.id) === attrId);

    return {
      id: attrId,
      code: attr.attribute_code || attr.name || '',
      label: attr.attribute_label || attr.name || '',
      data_type: attr.attribute_type || attr.type || 'text',
      is_localisable: attrDef?.is_localisable || false,
      is_scopable: attrDef?.is_scopable || false,
      is_required: attrDef?.is_required || false,
      validation_rule: attrDef?.validation_rule,
      allowed_units: attrDef?.allowed_units,
      default_unit: attrDef?.default_unit,
      default_currency: attrDef?.default_currency,
      allowed_html_tags: attrDef?.allowed_html_tags,
      allowed_file_types: attrDef?.allowed_file_types,
      max_file_size: attrDef?.max_file_size,
      allow_negative_numbers: attrDef?.allow_negative_numbers,
    };
  };

  // Convert legacy attribute value to enhanced attribute value
  const convertToEnhancedValue = (attr: Attribute): EnhancedAttributeValue => {
    return {
      id: Number(attr.id),
      attribute: Number((attr as any).attribute ?? attr.id),
      value: attr.value,
      locale: attr.locale,
      channel: attr.channel,
    };
  };

  // Handle adding new attribute to product
  const handleAddAttributeToProduct = async (
    attribute: EnhancedAttribute,
    locale?: string | null,
    channel?: string | null
  ) => {
    try {
      // Only send locale/channel if the attribute supports them
      const createLocale =
        attribute.is_localisable && (locale || selectedLocale) !== 'default'
          ? locale || selectedLocale
          : undefined;
      const createChannel =
        attribute.is_scopable && (channel || selectedChannel) !== 'default'
          ? channel || selectedChannel
          : undefined;

      await createMutation.mutateAsync({
        attributeId: attribute.id,
        value: '', // Start with empty value
        locale: createLocale,
        channel: createChannel,
      });

      setIsAddAttributeModalOpen(false);
      toast.success(`Added ${attribute.label} to product`);
    } catch (error) {
      logError(
        'Failed to add attribute to product',
        {
          component: 'ProductAttributesPanel',
          action: 'handleAddAttributeToProduct',
          metadata: { attributeId: attribute.id, productId, error },
        },
        { showToast: false, userFacing: false }
      );
      toast.error('Failed to add attribute to product');
      throw error;
    }
  };

  // Handle creating new global attribute
  const handleCreateNewAttribute = async (data: any) => {
    try {
      const response = await axiosInstance.post('/api/attributes/', data);
      // Invalidate attributes cache to refresh the list
      queryClient.invalidateQueries({ queryKey: ['attributes'] });
      toast.success(`Created new attribute: ${data.label}`);
      return response.data;
    } catch (error) {
      logError(
        'Failed to create new attribute',
        {
          component: 'ProductAttributesPanel',
          action: 'handleCreateNewAttribute',
          metadata: { data, error },
        },
        { showToast: false, userFacing: false }
      );
      toast.error('Failed to create new attribute');
      throw error;
    }
  };

  const groupNames = Object.keys(grouped);
  // filterGroup comes from pill selection – when set we show only that group
  const [filterGroup, setFilterGroup] = useState<string | undefined>(undefined);
  // openGroup controls accordion panel open state
  const [openGroup, setOpenGroup] = useState<string | undefined>(undefined);

  // whenever filterGroup changes sync openGroup
  useEffect(() => {
    if (filterGroup) {
      setOpenGroup(filterGroup);
    }
  }, [filterGroup]);

  // ensure openGroup exists among visible groups
  useEffect(() => {
    const visible = filterGroup ? [filterGroup] : groupNames;
    if (openGroup && !visible.includes(openGroup)) {
      setOpenGroup(undefined);
    }
  }, [filterGroup, groupNames, openGroup]);

  const visibleGroupNames = filterGroup ? [filterGroup] : groupNames;

  // -------------------------------------------------------------
  // Add-attribute dialog state
  // -------------------------------------------------------------
  const [addDialogGroup, setAddDialogGroup] = useState<string | null>(null);
  const [newAttrId, setNewAttrId] = useState<number | null>(null);
  const [, setNewAttrValue] = useState<string>('');

  const [addingAllGroup, setAddingAllGroup] = useState<string | undefined>(undefined);

  // Compute missing groups (by id): show button if group is not in product's group list
  const productGroupIds = new Set(
    Object.keys(grouped).filter((name: string) => name !== getAttributesLabels.groups.ungrouped)
  );
  const missingGroups = (sourceGroups as AttributeGroup[]).filter(
    (g: AttributeGroup) => !productGroupIds.has(g.name)
  );

  // State for Delete Group modal
  const [isDeleteGroupModalOpen, setIsDeleteGroupModalOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<string | undefined>(undefined);

  // Function to delete a group from the product
  const handleDeleteGroup = async (groupName: string) => {
    // Get all attribute IDs for this group
    const attrs = grouped[groupName] || [];
    if (attrs.length === 0) {
      toast.error(getAttributesLabels.groups.addError);
      return;
    }

    // Confirm with the user
    const attrPromises = attrs.map(attr => deleteMutation.mutateAsync({ id: String(attr.id) }));

    try {
      await Promise.all(attrPromises);
      await queryClient.invalidateQueries({
        queryKey: ['attributes', productId, selectedLocale, selectedChannel],
      });
      toast.success(getAttributesLabels.notifications.groupDeleted);
      setIsDeleteGroupModalOpen(false);
      setGroupToDelete(undefined);
    } catch {
      toast.error(getAttributesLabels.notifications.deleteFailed);
    }
  };

  // Helper: add all attributes from a group to the product
  async function handleAddGroupToProduct(group: AttributeGroup) {
    if (!group || !group.items || !productId) return;

    setAddingAllGroup(group.name);
    try {
      const attrIds = group.items
        .map((item: any) => item.attribute)
        .filter((id: any) => id != null);

      if (attrIds.length === 0) {
        toast.error(getAttributesLabels.groups.addError);
        return;
      }

      // Create a blank value for each attribute - users can edit after adding
      const promises = attrIds.map((attrId: number) => {
        // Get the attribute definition to check if it supports locale/channel
        const attrDef = allAttributes.find((a: any) => Number(a.id) === attrId);

        // Only send locale/channel if the attribute supports them
        const createLocale =
          attrDef?.is_localisable && selectedLocale !== 'default' ? selectedLocale : undefined;
        const createChannel =
          attrDef?.is_scopable && selectedChannel !== 'default' ? selectedChannel : undefined;

        return createMutation.mutateAsync({
          attributeId: attrId,
          value: '',
          locale: createLocale,
          channel: createChannel,
        });
      });

      await Promise.all(promises);
      await queryClient.invalidateQueries({
        queryKey: ['attributes', productId, selectedLocale, selectedChannel],
      });
      toast.success(getAttributesLabels.groups.addSuccess);
    } catch {
      toast.error(getAttributesLabels.groups.addError);
    } finally {
      setAddingAllGroup(undefined);
    }
  }

  // Helper to format attribute values for display in the table
  function formatAttributeValue(attr: any): string {
    const type = attr.attribute_type || attr.type;
    const value = attr.value;
    if (value === null || value === undefined || value === '') return '—';
    switch (type) {
      case 'boolean':
        return value
          ? t('settingspage.attributeValueRow.labels.boolean.yes', 'Yes')
          : t('settingspage.attributeValueRow.labels.boolean.no', 'No');
      case 'price':
        if (typeof value === 'object' && value.amount != null && value.currency) {
          return `${value.amount} ${value.currency}`;
        }
        return '—';
      case 'measurement':
        if (typeof value === 'object' && value.amount != null && value.unit) {
          return `${value.amount} ${value.unit}`;
        }
        return '—';
      case 'rich_text':
        return typeof value === 'string' ? value.replace(/<[^>]+>/g, '').slice(0, 80) : '—';
      default:
        return typeof value === 'object' ? JSON.stringify(value) : String(value);
    }
  }

  // Add function wrappers to handle type issues
  const handleLocaleChange = (locale: string) => {
    if (locales.some(l => l.code === locale)) {
      setSelectedLocale(locale as LocaleCode);
    }
  };

  const handleChannelChange = (channel: string) => {
    if (channels.some(c => c.code === channel)) {
      setSelectedChannel(channel as ChannelCode);
    }
  };

  // Feature flag check - use new effective attributes pipeline if enabled
  // IMPORTANT: This check is placed AFTER all hooks to comply with Rules of Hooks
  if (ENABLE_EFFECTIVE_ATTRIBUTES_V2) {
    return (
      <EffectiveAttributesView
        productId={productId}
        locale={locale}
        channel={channel}
        enabled={enabled}
      />
    );
  }

  // loading state includes family groups
  const isLoading = isLoadingFamilyGroups || isPending;
  const hasError = !!familyError || isError;

  // MODIFIED: Relax the bailout condition to allow rendering in create mode
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-6 animate-pulse rounded bg-muted" />
        ))}
      </div>
    );
  }

  if (hasError) {
    return <p className="text-destructive">{getAttributesLabels.errors.loadError}</p>;
  }

  return (
    <div className="space-y-4">
      {hasFamily && hasNoGroups && (
        <div className="mb-4 rounded-md border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/30">
          <h3 className="mb-1 font-medium text-yellow-800 dark:text-yellow-200">
            {getAttributesLabels.panel.noGroups}
          </h3>
          <p className="text-sm text-yellow-700 dark:text-yellow-300">
            {getAttributesLabels.panel.emptyState.description}
          </p>
          <div className="mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                // Navigate to family management page (adjust the URL as needed)
                window.open(`/families/${familyId}`, '_blank');
              }}
            >
              Configure Family
            </Button>
          </div>
          <p className="mt-2 text-xs text-yellow-600 dark:text-yellow-400">
            You can add attribute groups to your family to enable product attribute management
          </p>
        </div>
      )}

      {/* Only show LocaleChannelSelector if there are localizable or scopable attributes */}
      {(() => {
        // Check if any attributes in any group are localizable or scopable
        const hasLocalizableOrScopable = Object.values(grouped).some((attrs: Attribute[]) =>
          attrs.some((attr: any) => {
            const attrId = Number((attr as any).attribute ?? attr.id);
            const attrDef = allAttributes.find((a: any) => Number(a.id) === attrId);
            return attrDef?.is_localisable || attrDef?.is_scopable;
          })
        );

        if (!hasLocalizableOrScopable) return null;

        return (
          <LocaleChannelSelector
            selectedLocale={selectedLocale}
            selectedChannel={selectedChannel}
            onLocaleChange={handleLocaleChange}
            onChannelChange={handleChannelChange}
          />
        );
      })()}

      {/* Group management UI */}
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-medium">{getAttributesLabels.panel.title}</h3>
        {!isCreateMode &&
          Object.keys(grouped).filter(name => name !== getAttributesLabels.groups.ungrouped)
            .length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30"
              onClick={() => setIsDeleteGroupModalOpen(true)}
            >
              {getAttributesLabels.buttons.deleteGroup}
            </Button>
          )}
      </div>

      {/* Group navigation */}
      {groupNames.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {groupNames.map(g => (
            <Button
              key={g}
              variant={g === filterGroup ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setFilterGroup(filterGroup === g ? undefined : g)}
            >
              {g}
            </Button>
          ))}
        </div>
      )}

      {/* Add missing groups UI */}
      {missingGroups.length > 0 ? (
        <div className="mb-2 flex flex-wrap gap-2">
          {missingGroups.map((group: AttributeGroup) => (
            <Tooltip key={group.id}>
              <TooltipTrigger asChild>
                <Button
                  variant="primary"
                  size="sm"
                  className="flex items-center gap-2 bg-gradient-to-tr from-primary to-blue-500 px-3 py-2 text-white shadow-md hover:from-blue-600 hover:to-primary focus:ring-2 focus:ring-primary/50"
                  aria-label={`Add group '${group.name}' to product`}
                  disabled={addingAllGroup === group.name}
                  onClick={() => handleAddGroupToProduct(group)}
                >
                  {addingAllGroup === group.name ? (
                    <span className="size-4 animate-spin">
                      <Layers className="size-4" />
                    </span>
                  ) : (
                    <Layers className="size-4" />
                  )}
                  <span>{getAttributesLabels.groups.addGroup.replace('{{name}}', group.name)}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{getAttributesLabels.groups.addTooltip}</TooltipContent>
            </Tooltip>
          ))}
        </div>
      ) : missingGroups.length === 0 && sourceGroups.length > 0 ? (
        <div className="mb-2 text-xs text-muted-foreground">
          {getAttributesLabels.groups.allGroupsAdded}
        </div>
      ) : null}

      {/* Add Attribute Dialog */}
      {addDialogGroup && (
        <Dialog
          open
          onOpenChange={open => {
            if (!open) {
              setAddDialogGroup(null);
              setNewAttrId(null);
              setNewAttrValue('');
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{getAttributesLabels.buttons.addAttribute}</DialogTitle>
              <DialogDescription>
                {getAttributesLabels.addAttributeModal.description}
              </DialogDescription>
            </DialogHeader>

            {/* Attribute select - simplified to just the dropdown */}
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">
                  {getAttributesLabels.buttons.addAttribute}
                </label>
                <select
                  className="w-full rounded border border-border bg-background px-2 py-1 text-foreground"
                  value={newAttrId ?? ''}
                  onChange={e => setNewAttrId(Number(e.target.value))}
                >
                  <option value="" disabled>
                    {getAttributesLabels.addAttributeModal.selectAttribute}
                  </option>
                  {(() => {
                    const grp = sourceGroups.find((g: AttributeGroup) => g.name === addDialogGroup);
                    if (!grp) return null;
                    // Filter attributeIds already present in product
                    const presentIds = new Set(
                      (productData?.attributes as Attribute[] | undefined)?.map((a: Attribute) => {
                        const attrId = (a as any).attribute ?? a.id;
                        return typeof attrId === 'string' ? Number(attrId) : attrId;
                      }) || []
                    );
                    return grp.items
                      .filter((item: any) => !presentIds.has(item.attribute))
                      .map((item: any) => {
                        const attrMeta = allAttributes.find(
                          (a: Attribute) =>
                            a.id === String(item.attribute) || Number(a.id) === item.attribute
                        );
                        const label =
                          (attrMeta?.attribute_label as any) ||
                          (attrMeta as any)?.label ||
                          attrMeta?.name ||
                          (attrMeta as any)?.code ||
                          attrMeta?.attribute_code ||
                          `Attr ${item.attribute}`;
                        return (
                          <option key={item.attribute} value={item.attribute}>
                            {label}
                          </option>
                        );
                      });
                  })()}
                </select>
              </div>
            </div>

            <DialogFooter className="mt-4">
              <Button
                onClick={async () => {
                  if (!newAttrId) return;

                  try {
                    // Get the attribute definition to check if it supports locale/channel
                    const attrDef = allAttributes.find((a: any) => Number(a.id) === newAttrId);

                    // Only send locale/channel if the attribute supports them
                    const createLocale =
                      attrDef?.is_localisable && selectedLocale !== 'default'
                        ? selectedLocale
                        : undefined;
                    const createChannel =
                      attrDef?.is_scopable && selectedChannel !== 'default'
                        ? selectedChannel
                        : undefined;

                    // Add attribute with empty value - users will edit it later
                    await createMutation.mutateAsync({
                      attributeId: newAttrId,
                      value: '',
                      locale: createLocale,
                      channel: createChannel,
                    });

                    // 🔄 REFRESH DATA
                    await queryClient.invalidateQueries({
                      queryKey: ['attributes', productId, selectedLocale, selectedChannel],
                    });

                    setAddDialogGroup(null);

                    // Show success message with hint to edit
                    toast.success(getAttributesLabels.addAttributeModal.success);
                  } catch {
                    toast.error(getAttributesLabels.addAttributeModal.error);
                  }
                }}
                disabled={!newAttrId || createMutation.isPending}
              >
                {getAttributesLabels.addAttributeModal.buttons.add}
              </Button>
              <DialogClose asChild>
                <Button variant="outline">
                  {getAttributesLabels.addAttributeModal.buttons.cancel}
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Group Modal */}
      <Dialog open={isDeleteGroupModalOpen} onOpenChange={setIsDeleteGroupModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{getAttributesLabels.deleteGroupModal.title}</DialogTitle>
            <DialogDescription className="text-amber-600 dark:text-amber-400">
              {getAttributesLabels.deleteGroupModal.warning}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">
                {getAttributesLabels.deleteGroupModal.selectLabel}
              </label>
              <select
                className="w-full rounded border border-border bg-background px-3 py-2 text-foreground"
                value={groupToDelete || ''}
                onChange={e => setGroupToDelete(e.target.value)}
              >
                <option value="" disabled>
                  {getAttributesLabels.deleteGroupModal.selectPlaceholder}
                </option>
                {Object.keys(grouped)
                  .filter(
                    name =>
                      name !== getAttributesLabels.groups.ungrouped && grouped[name].length > 0
                  )
                  .map(name => (
                    <option key={name} value={name}>
                      {name} ({grouped[name].length} attributes)
                    </option>
                  ))}
              </select>
            </div>

            {groupToDelete && (
              <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm dark:border-amber-800 dark:bg-amber-900/30">
                <p className="mb-1 font-medium text-amber-800 dark:text-amber-200">
                  {getAttributesLabels.deleteGroupModal.aboutToDelete}
                </p>
                <p className="text-amber-700 dark:text-amber-300">
                  {getAttributesLabels.deleteGroupModal.allAttributesIn}{' '}
                  {grouped[groupToDelete]?.length || 0}{' '}
                  {getAttributesLabels.deleteGroupModal.attributesIn} "{groupToDelete}"
                </p>
                <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                  {getAttributesLabels.deleteGroupModal.actionDescription}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="destructive"
              onClick={() => groupToDelete && handleDeleteGroup(groupToDelete)}
              disabled={!groupToDelete || deleteMutation.isPending}
            >
              {deleteMutation.isPending
                ? getAttributesLabels.deleteGroupModal.buttons.deleting
                : getAttributesLabels.deleteGroupModal.buttons.confirm}
            </Button>
            <Button variant="outline" onClick={() => setIsDeleteGroupModalOpen(false)}>
              {getAttributesLabels.deleteGroupModal.buttons.cancel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Accordion-based group rendering */}
      <Accordion
        type="single"
        collapsible
        value={openGroup}
        onValueChange={setOpenGroup}
        className="w-full"
      >
        {visibleGroupNames.map((groupName: string) => {
          const attrs = grouped[groupName];
          // Find all attribute IDs in this group
          const groupMeta = sourceGroups.find((g: AttributeGroup) => g.name === groupName);
          const allGroupAttrIds = groupMeta
            ? groupMeta.items.map((item: any) => item.attribute)
            : [];
          // Find which are not present in product
          const presentIds = new Set(
            (productData?.attributes as Attribute[] | undefined)?.map((a: Attribute) => {
              const attrId = (a as any).attribute ?? a.id;
              return typeof attrId === 'string' ? Number(attrId) : attrId;
            }) || []
          );
          const unusedAttrIds = allGroupAttrIds.filter((attrId: number) => !presentIds.has(attrId));
          const canAddAll = unusedAttrIds.length > 0;
          return (
            <AccordionItem value={groupName} key={groupName} className="border-b">
              <AccordionTrigger className="flex items-center justify-between px-4 py-3">
                <span className="font-medium">
                  {groupName} ({attrs.length})
                </span>
                <div className="flex items-center gap-2">
                  {canAddAll && !isCreateMode && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          role="button"
                          tabIndex={0}
                          className="flex size-8 cursor-pointer items-center justify-center rounded-md bg-gradient-to-tr from-primary to-blue-500 p-0 text-white shadow-md outline-none hover:from-blue-600 hover:to-primary focus:ring-2 focus:ring-primary/50"
                          aria-label={`Add all attributes from ${groupName}`}
                          onClick={async e => {
                            e.stopPropagation();
                            setAddingAllGroup(groupName);
                            try {
                              const promises = unusedAttrIds.map((attrId: number) => {
                                // Get the attribute definition to check if it supports locale/channel
                                const attrDef = allAttributes.find(
                                  (a: any) => Number(a.id) === attrId
                                );

                                // Only send locale/channel if the attribute supports them
                                const createLocale =
                                  attrDef?.is_localisable && selectedLocale !== 'default'
                                    ? selectedLocale
                                    : undefined;
                                const createChannel =
                                  attrDef?.is_scopable && selectedChannel !== 'default'
                                    ? selectedChannel
                                    : undefined;

                                return createMutation.mutateAsync({
                                  attributeId: attrId,
                                  value: '',
                                  locale: createLocale,
                                  channel: createChannel,
                                });
                              });
                              await Promise.all(promises);
                              // 🔄 REFRESH DATA
                              await queryClient.invalidateQueries({
                                queryKey: [
                                  'attributes',
                                  productId,
                                  selectedLocale,
                                  selectedChannel,
                                ],
                              });
                              toast.success(
                                `${getAttributesLabels.groups.addSuccess} '${groupName}'`
                              );
                            } catch {
                              toast.error(getAttributesLabels.groups.addError);
                            } finally {
                              setAddingAllGroup(undefined);
                            }
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.currentTarget.click();
                            }
                          }}
                        >
                          {addingAllGroup === groupName ? (
                            <span className="mx-auto size-4 animate-spin">
                              <Sparkles className="mx-auto size-4" />
                            </span>
                          ) : (
                            <Sparkles className="mx-auto size-4" />
                          )}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{getAttributesLabels.groups.addTooltip}</TooltipContent>
                    </Tooltip>
                  )}
                  {!isCreateMode && (
                    <span
                      role="button"
                      tabIndex={0}
                      className="flex size-8 cursor-pointer items-center justify-center rounded-md p-0 hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={e => {
                        e.stopPropagation();
                        setAddDialogGroup(groupName);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          setAddDialogGroup(groupName);
                        }
                      }}
                      aria-label={`${getAttributesLabels.buttons.addAttribute}`}
                    >
                      <PlusIcon className="size-4" />
                    </span>
                  )}
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-2 pb-4">
                {attrs.length === 0 ? (
                  // Instead of trying to render individual attribute rows which causes linter errors,
                  // Just show an improved message explaining how to add attributes
                  <div className="flex flex-col gap-2 p-4 text-sm text-muted-foreground">
                    <p>{getAttributesLabels.panel.emptyState.title}</p>
                    {isCreateMode ? (
                      <p>{getAttributesLabels.panel.emptyState.description}</p>
                    ) : (
                      <p>{getAttributesLabels.buttons.addAttribute}</p>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table className="min-w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-1/4">
                            {getAttributesLabels.panel.tableHeaders.name}
                          </TableHead>
                          <TableHead className="w-1/12">
                            {getAttributesLabels.panel.tableHeaders.type}
                          </TableHead>
                          {/* Only show Locale column if any attribute in this group is localizable */}
                          {attrs.some((attr: any) => {
                            // Get the actual attribute definition to check flags
                            const attrId = Number((attr as any).attribute ?? attr.id);
                            const attrDef = allAttributes.find((a: any) => Number(a.id) === attrId);
                            return attrDef?.is_localisable;
                          }) && (
                            <TableHead className="w-1/12">
                              {getAttributesLabels.panel.tableHeaders.locale}
                            </TableHead>
                          )}
                          {/* Only show Channel column if any attribute in this group is scopable */}
                          {attrs.some((attr: any) => {
                            // Get the actual attribute definition to check flags
                            const attrId = Number((attr as any).attribute ?? attr.id);
                            const attrDef = allAttributes.find((a: any) => Number(a.id) === attrId);
                            return attrDef?.is_scopable;
                          }) && (
                            <TableHead className="w-1/12">
                              {getAttributesLabels.panel.tableHeaders.channel}
                            </TableHead>
                          )}
                          <TableHead>{getAttributesLabels.panel.tableHeaders.value}</TableHead>
                          <TableHead className="w-20 text-right">
                            {getAttributesLabels.panel.tableHeaders.actions}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {attrs.map((attr: Attribute) => {
                          const attrId = Number((attr as any).attribute ?? attr.id);
                          const enhancedAttr = convertToEnhancedAttribute(attr);
                          const enhancedValue = convertToEnhancedValue(attr);
                          const isCurrentlyEditing = editingAttributeId === attrId;

                          return (
                            <TableRow key={attr.id}>
                              <TableCell className="py-4" colSpan={6}>
                                {isCurrentlyEditing ? (
                                  <EnhancedAttributeValueEditor
                                    attribute={enhancedAttr}
                                    value={enhancedValue}
                                    isEditable={true}
                                    isNew={false}
                                    onSave={handleEnhancedSave}
                                    onCancel={handleEnhancedCancel}
                                    selectedLocale={selectedLocale}
                                    selectedChannel={selectedChannel}
                                    isPending={updateMutation.isPending || createMutation.isPending}
                                  />
                                ) : (
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1">
                                      <div className="grid grid-cols-4 items-center gap-4">
                                        <div>
                                          <div className="font-medium">
                                            {attr.attribute_label ||
                                              attr.name ||
                                              attr.attribute_code}
                                          </div>
                                          <div className="text-sm text-muted-foreground">
                                            {attr.attribute_type || attr.type}
                                          </div>
                                        </div>

                                        {/* Locale info */}
                                        <div className="text-sm">
                                          {enhancedAttr.is_localisable
                                            ? attr.locale ||
                                              getAttributesLabels.panel.localeChannel.allLocales
                                            : getAttributesLabels.panel.localeChannel.notApplicable}
                                        </div>

                                        {/* Channel info */}
                                        <div className="text-sm">
                                          {enhancedAttr.is_scopable
                                            ? attr.channel ||
                                              getAttributesLabels.panel.localeChannel.allChannels
                                            : getAttributesLabels.panel.localeChannel.notApplicable}
                                        </div>

                                        {/* Value display */}
                                        <div className="text-sm">{formatAttributeValue(attr)}</div>
                                      </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center space-x-2">
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setEditingAttributeId(attrId)}
                                            aria-label={
                                              getAttributesLabels.panel.tooltips.editAttribute
                                            }
                                          >
                                            <PencilIcon className="size-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {getAttributesLabels.panel.tooltips.editAttribute}
                                        </TooltipContent>
                                      </Tooltip>

                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() =>
                                              deleteMutation.mutate({
                                                id: attr.id,
                                              })
                                            }
                                            disabled={deleteMutation.isPending}
                                            aria-label={
                                              getAttributesLabels.panel.tooltips.deleteAttribute
                                            }
                                          >
                                            <Trash2 className="size-4" />
                                          </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                          {getAttributesLabels.panel.tooltips.deleteAttribute}
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Enhanced Add Attribute Modal */}
      <AddAttributeModal
        isOpen={isAddAttributeModalOpen}
        onOpenChange={setIsAddAttributeModalOpen}
        availableAttributes={allAttributes.filter((attr: any) => {
          // Filter out attributes that are already added to the product
          const presentIds = new Set(
            (attributesHook.data?.attributes || []).map((a: Attribute) => {
              const attrId = (a as any).attribute ?? a.id;
              return typeof attrId === 'string' ? Number(attrId) : attrId;
            })
          );
          return !presentIds.has(Number(attr.id));
        })}
        onAddAttribute={handleAddAttributeToProduct}
        onCreateAttribute={handleCreateNewAttribute}
        isPending={createMutation.isPending}
        selectedLocale={selectedLocale}
        selectedChannel={selectedChannel}
        groupId={null}
        attributeValues={{}}
      />

      {/* Enhanced Create Attribute Modal */}
      <EnhancedAttributeModal
        isOpen={isCreateAttributeModalOpen}
        onOpenChange={setIsCreateAttributeModalOpen}
        onSubmit={handleCreateNewAttribute}
        mode="create"
        isPending={false}
      />

      {/* Add new attribute button */}
      {!isCreateMode && (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={() => setIsAddAttributeModalOpen(true)}
            className="w-full"
          >
            <PlusIcon className="mr-2 size-4" />
            Add Attribute to Product
          </Button>
        </div>
      )}
    </div>
  );
}
